import React from 'react';
import { Modal, Button, Space } from 'antd';
import styled from 'styled-components';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { useDeleteCustomTacticMutation } from 'redux/apiSlice/pillarsActionPlan.slice';
import { TTacticTask } from '../../TacticTable.types';

type TDeleteTacticModalProps = {
  open: boolean;
  onClose: () => void;
  tactic: TTacticTask | null;
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    box-shadow: none !important;
  }
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #000000;
`;

const ModalContent = styled.p`
  font-size: 16px;
  padding: 8px 24px;
  line-height: 1.5;
  color: #414042;
`;

const CancelButton = styled(Button)`
  display: flex;
  padding: 0px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--Main-border, #dedede);
  background: white;
  color: rgba(0, 0, 0, 0.85);
  &:hover {
    background: #e6e6e6;
    border-color: #c4c4c4;
  }
`;

const RemoveButton = styled(Button)`
  display: flex;
  padding: 0px 16px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: var(--Error-Active, #c71a14);
  color: white;
  border: none;
  &:hover {
    background: #9e1410 !important;
    color: white !important;
  }
  .ant-btn-loading-icon {
    display: inline-flex;
    align-items: center;
    margin-right: 8px;
  }
`;

const DeleteTacticModal: React.FC<TDeleteTacticModalProps> = ({
  open,
  onClose,
  tactic,
}) => {
  const { showToastNotification } = useShowToastNotification();
  const [deleteCustomTactic, { isLoading }] = useDeleteCustomTacticMutation();

  const handleDelete = async () => {
    if (!tactic?.actionPlanId) {
      onClose();
      return;
    }

    try {
      await deleteCustomTactic({
        actionPlanId: tactic.actionPlanId,
      }).unwrap();

      showToastNotification({
        message: `Tactic "${tactic.tacticTitle}" has been removed successfully.`,
        type: ENotificationType.SUCCESS,
      });

      onClose();
    } catch (error: any) {
      showToastNotification({
        message:
          error?.data?.message || 'Failed to remove tactic. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  };

  const incompleteTaskCount = tactic
    ? tactic.todoCount + tactic.inProgressCount + tactic.stalledCount
    : 0;

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      footer={null}
      closable
      width={544}
      centered
      data-cy="delete_confirm_modal"
      title={
        <ModalTitle
          style={{
            paddingTop: '16px',
            paddingRight: '24px',
            paddingBottom: '8px',
            paddingLeft: '24px',
          }}
        >
          Remove the Tactic
        </ModalTitle>
      }
      modalRender={node => {
        const element = node as React.ReactElement<any>;
        return React.cloneElement(element, {
          style: {
            ...(element.props.style || {}),
            padding: 0,
          },
        } as any);
      }}
    >
      <ModalContent>
        This Tactic includes{' '}
        <span style={{ fontWeight: 600 }}>
          {incompleteTaskCount} incomplete task.{' '}
        </span>
        <br />
        Are you sure you want to remove this?
      </ModalContent>
      <Space
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '0px 24px',
          paddingBottom: '16px',
        }}
      >
        <CancelButton onClick={onClose} data-cy="cancel_btn">
          Cancel
        </CancelButton>
        <RemoveButton
          onClick={handleDelete}
          loading={isLoading}
          data-cy="confirm_btn"
        >
          Remove
        </RemoveButton>
      </Space>
    </StyledModal>
  );
};

export default DeleteTacticModal;
